import { z } from 'zod'
import { schemas } from '@repo/contracts'
import { apiErrorHandler } from '../errorHandler'
import { parseDataOfType } from '../../common/parseDataType'

// -- UI enum value maps (kept hand-written; components reference e.g. ProposalSectionKind.Rewrite).
// The generated z.enum(...) inside the schemas governs wire parsing; these maps are UI conveniences
// and stay type-compatible (same string-literal union). Each includes the backend 'unknown'
// zero-value. --

export const ProposalStatus = {
  Unknown: 'unknown',
  Drafting: 'drafting',
  Ready: 'ready',
  Exported: 'exported'
} as const
export type ProposalStatus = (typeof ProposalStatus)[keyof typeof ProposalStatus]

export const ProposalSectionKind = {
  Unknown: 'unknown',
  Boilerplate: 'boilerplate',
  Rewrite: 'rewrite',
  Manual: 'manual',
  Cover: 'cover'
} as const
export type ProposalSectionKind = (typeof ProposalSectionKind)[keyof typeof ProposalSectionKind]

export const ProposalSectionState = {
  Unknown: 'unknown',
  Pending: 'pending',
  Drafted: 'drafted',
  Ready: 'ready'
} as const
export type ProposalSectionState = (typeof ProposalSectionState)[keyof typeof ProposalSectionState]

export const ProposalSectionMessageAuthor = {
  Unknown: 'unknown',
  System: 'system',
  User: 'user',
  Assistant: 'assistant',
  UserMention: 'userMention'
} as const
export type ProposalSectionMessageAuthor =
  (typeof ProposalSectionMessageAuthor)[keyof typeof ProposalSectionMessageAuthor]

// -- Wire types (generated; @repo/contracts is the source of truth) --

export type Proposal = z.infer<typeof schemas.apihttp_ProposalResponse>
export type ProposalSection = z.infer<typeof schemas.apihttp_ProposalSectionResponse>
export type ProposalSectionMessage = z.infer<typeof schemas.apihttp_ProposalSectionMessageResponse>
export type ProposalSource = z.infer<typeof schemas.apihttp_ProposalSourceResponse>
export type CreateProposalRequest = z.input<typeof schemas.apihttp_CreateProposalRequest>
export type ExportProposalResponse = z.infer<typeof schemas.apihttp_ExportProposalResponse>
export type AgentRun = z.infer<typeof schemas.apihttp_AgentRunResponse>
export type StartProposalBuildRunRequest = z.input<typeof schemas.apihttp_StartProposalBuildRunRequest>

// -- SSE message schema (carved out: not generated; hand-written on both sides) --

export const ProposalSseType = {
  Content: 'content',
  Status: 'status',
  Error: 'error',
  // Warning: an advisory frame attributable to a section (e.g. a loud computed-query failure or a
  // budget shortfall) that must persist after that section's content has finished streaming, unlike
  // Status (treated as transient pre-stream progress text). Mirrors the backend's SseTypeWarning.
  Warning: 'warning'
} as const

export const ProposalSseResponseSchema = z.object({
  proposalId: z.number(),
  sectionId: z.number(),
  response: z.string(),
  sseType: z.enum(ProposalSseType)
})
export type ProposalSseResponse = z.infer<typeof ProposalSseResponseSchema>

// -- Functions (build/refine stream via FetchEventTarget, not here) --

export async function listProposals(projectId: number): Promise<z.infer<typeof schemas.apihttp_ListProposalsResponse>> {
  const res = await fetch(`/api/projects/${projectId}/proposals`, { method: 'GET', credentials: 'same-origin' })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_ListProposalsResponse)
}

export async function getProposal(projectId: number, proposalId: number): Promise<Proposal> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_ProposalResponse)
}

export async function startProposalBuildRun(
  projectId: number,
  proposalId: number,
  req: StartProposalBuildRunRequest
): Promise<AgentRun> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/build-runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(req)
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_AgentRunResponse)
}

export async function getLatestProposalBuildRun(projectId: number, proposalId: number): Promise<AgentRun | null> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/build-runs/latest`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  if (res.status === 204) return null
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_AgentRunResponse)
}

export async function getProposalBuildRun(
  projectId: number,
  proposalId: number,
  runId: number,
  signal?: AbortSignal
): Promise<AgentRun> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/build-runs/${runId}`, {
    method: 'GET',
    credentials: 'same-origin',
    ...(signal ? { signal } : {})
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_AgentRunResponse)
}

export async function cancelProposalBuildRun(projectId: number, proposalId: number, runId: number): Promise<AgentRun> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/build-runs/${runId}/cancel`, {
    method: 'POST',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_AgentRunResponse)
}

export async function retryProposalBuildRun(projectId: number, proposalId: number, runId: number): Promise<AgentRun> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/build-runs/${runId}/retry`, {
    method: 'POST',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_AgentRunResponse)
}

export async function createProposal(projectId: number, req: CreateProposalRequest): Promise<Proposal> {
  const res = await fetch(`/api/projects/${projectId}/proposals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(req)
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_ProposalResponse)
}

export async function getProposalSources(projectId: number, proposalId: number): Promise<Array<ProposalSource>> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/sources`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_ListProposalSourcesResponse).sources
}

export async function replaceProposalSources(
  projectId: number,
  proposalId: number,
  docIds: Array<number>
): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/sources`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ docIds })
  })
  await apiErrorHandler(res)
}

export async function deleteProposal(projectId: number, proposalId: number): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}`, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}

export async function getSectionMessages(
  projectId: number,
  proposalId: number,
  sectionId: number
): Promise<Array<ProposalSectionMessage>> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/sections/${sectionId}/messages`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_ListSectionMessagesResponse).messages
}

export async function selectSectionVersion(
  projectId: number,
  proposalId: number,
  sectionId: number,
  messageId: number
): Promise<ProposalSection> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/sections/${sectionId}/select`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ messageId })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_ProposalSectionResponse)
}

export async function deselectSectionVersion(
  projectId: number,
  proposalId: number,
  sectionId: number
): Promise<ProposalSection> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/sections/${sectionId}/deselect`, {
    method: 'POST',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_ProposalSectionResponse)
}

export async function renameProposal(
  projectId: number,
  proposalId: number,
  documentName: string
): Promise<z.infer<typeof schemas.apihttp_RenameProposalResponse>> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ documentName })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_RenameProposalResponse)
}

export async function editSection(
  projectId: number,
  proposalId: number,
  sectionId: number,
  draftMarkdown: string
): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/sections/${sectionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ draftMarkdown })
  })
  await apiErrorHandler(res)
}

export async function createSection(projectId: number, proposalId: number, heading: string): Promise<ProposalSection> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/sections`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ heading })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_ProposalSectionResponse)
}

export async function renameSection(
  projectId: number,
  proposalId: number,
  sectionId: number,
  heading: string
): Promise<ProposalSection> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/sections/${sectionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ heading })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_ProposalSectionResponse)
}

export async function deleteSection(projectId: number, proposalId: number, sectionId: number): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/sections/${sectionId}`, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}

export async function reorderSections(projectId: number, proposalId: number, sectionIds: Array<number>): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/sections/order`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ sectionIds })
  })
  await apiErrorHandler(res)
}

export async function exportProposal(projectId: number, proposalId: number): Promise<ExportProposalResponse> {
  const res = await fetch(`/api/projects/${projectId}/proposals/${proposalId}/export`, {
    method: 'POST',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_ExportProposalResponse)
}
